/**
 * Which games are running on this machine, right now.
 *
 * The desktop shell already watches for this and the app already forwards it
 * to the server so friends can see it. Nothing kept a copy for the app's own
 * use, so a button that starts a game had no way to know the game was
 * already open - it offered to start Valorant while Valorant was in the
 * foreground.
 *
 * One list, written by the one place that receives the shell's report, read
 * by whoever needs it. Empty everywhere but the desktop app, where there is
 * nothing to watch.
 */

import { useSyncExternalStore } from 'react';

let list: string[] = [];
const watchers = new Set<() => void>();

/** Called by App when the shell reports. */
export function setPlaying(next: string[]): void {
  const same = next.length === list.length && next.every((id, i) => id === list[i]);
  if (same) return;
  list = next;
  for (const fn of watchers) fn();
}

function subscribe(fn: () => void): () => void {
  watchers.add(fn);
  return () => { watchers.delete(fn); };
}

/* The same array between reports, because useSyncExternalStore compares by
   identity and a fresh one every read is an infinite render. */
function snapshot(): string[] { return list; }

/** Every game running, in catalogue order. */
export function usePlaying(): string[] {
  return useSyncExternalStore(subscribe, snapshot, () => list);
}

/** Whether this one is running. */
export function useIsPlaying(gameId: string | null | undefined): boolean {
  const all = usePlaying();
  return !!gameId && all.includes(gameId);
}
